/**
 * External Imports
 */
import { FC, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuNavVertical } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { ChartLegend, CircPieChart } from '../metricsChart';
import { logListClick } from '../../utils/analyticsHelpers';

/**
 * Style Imports
 */
import { SideNavLabel } from './SideNav.styles';

interface SideNavProps {
  completeCount: number;
  completePercent: number;
  todoCount: number;
  todoPercent: number;
  totalCount: number;
  unableCount?: number;
  newHires?: number;
  selectedTab: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
}

const SideNav: FC<SideNavProps> = ({
  completeCount,
  completePercent,
  todoCount,
  todoPercent,
  totalCount,
  newHires,
  unableCount,
  selectedTab,
  setSelectedTab
}) => {
  const [data, setData] = useState<number[]>([]);

  const { logTrackingEvent } = useAnalyticsApi();

  const changeTab = (tab: string) => {
    switch (tab) {
      case 'To Do':
        logTrackingEvent(logListClick('to-do'));
        break;
      case 'Completed':
        logTrackingEvent(logListClick('completed'));
        break;
      case 'New Hires':
        logTrackingEvent(logListClick('new-hires'));
        break;
      default:
        console.log('No event for tab filter');
    }
    setSelectedTab(tab);
  };

  const { pathname } = useLocation();

  useEffect(() => {
    if (unableCount !== undefined) {
      setData([completeCount, todoCount, unableCount || 0]);
    } else {
      setData([completeCount, todoCount]);
    }
  }, [completeCount, todoCount, unableCount]);

  return (
    <NeuNavVertical
      id="Side-Nav"
      className="h-auto"
      kind="outlined"
      label="MENU"
      menu="blue"
      mode="drawer"
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.15) 0 2px 4px',
        '--nav-width-drawer': 'clamp(200px, 25vw, 200px)'
      }}
    >
      <NeuNavVertical
        unit="header"
        style={{
          padding:
            '0 calc(var(--nav-padding-x) / 1.5) calc(var(--nav-padding-y) / 0.75)'
        }}
      />
      <NeuNavVertical unit="content" style={{ overflow: 'clip' }}>
        <NeuNavVertical
          id="Side-Nav-All-Label"
          className="ps-2"
          active={selectedTab === 'All'}
          unit="item"
          onClick={() => changeTab('All')}
        >
          <SideNavLabel>All ({totalCount})</SideNavLabel>
        </NeuNavVertical>
        <NeuNavVertical
          id="Side-Nav-To-Do-Label"
          className="ps-2"
          active={selectedTab === 'To Do'}
          unit="item"
          onClick={() => changeTab('To Do')}
        >
          <SideNavLabel> To Do ({todoCount}) </SideNavLabel>
        </NeuNavVertical>
        <NeuNavVertical
          id="Side-Nav-Completed-Label"
          className="ps-2"
          active={selectedTab === 'Completed'}
          unit="item"
          onClick={() => changeTab('Completed')}
        >
          <SideNavLabel>Completed ({completeCount})</SideNavLabel>
        </NeuNavVertical>
        {/* ******************************************* */}
        {/* ********** Removing till Phase II ********* */}
        {/* ******************************************* */}
        {/* {pathname.includes('/patients') && (
        <NeuNavVertical
          id="Side-Nav-Unable-Label"
          active={selectedTab === 'Unable' }
          class="ps-2"
          onClick={() => changeTab('Unable')}
        >
          <NeuStepper  />
          <SideNavLabel>Unable to Round ({unableCount})</SideNavLabel>
        </NeuNavVertical>
      )} */}
        {pathname.includes('/employees') && (
          <NeuNavVertical
            id="Side-Nav-New-Hire-Label"
            className="ps-2"
            active={selectedTab === 'New Hires'}
            unit="item"
            onClick={() => changeTab('New Hires')}
          >
            <SideNavLabel>New Hires ({newHires})</SideNavLabel>
          </NeuNavVertical>
        )}
        <div className="d-flex flex-column px-0 mx-0">
          {/* dont draw the census metrics chart if no data */}
          {data.length > 0 && <CircPieChart data={data} />}
          {data.length > 0 && (
            <ChartLegend
              completeCount={completeCount}
              completePercent={completePercent}
              todoCount={todoCount}
              todoPercent={todoPercent}
            />
          )}
        </div>{' '}
      </NeuNavVertical>
    </NeuNavVertical>
  );
};

export default SideNav;
